/**
 * fireberryRowToNoteRecord — עדשת מראה גנרית מעל `fb_records`
 *
 * ⚠️ **הבאג שזה מתקן הוא בן-דודו של באג ה-09.08, שכבה אחת עמוק יותר.**
 *
 * ב-09.08 נרשמו שמונה ישויות מעל טבלאות ייעודיות, ו-`fetchNotes()` לא הכיר
 * ענף קריאה עבורן — התוצאה היתה רשימות ריקות. הפעם הענף כן קיים, אבל
 * הממפה שבתוכו — `fireberryAccountToNoteRecord` — **מקודד בית עסק**:
 *
 *   record_type: "fireberry_account"   ← קבוע, לכל שורה
 *   entity_type: "fireberry_account"   ← קבוע, לכל שורה
 *   meta: { phone, email, city, address, industry, account_number, ... }
 *
 * כלומר כל עדשת מראה שאיננה בית עסק היתה מציגה שורות — אבל מסומנות כבית
 * עסק, ועם רשימת שדות שאין לה שום קשר לתוכן. שורת היסטוריה תעסוקתית היתה
 * מאבדת את `pcfjobtitle`, `pcfjobdescription` ו-`pcfenddate` בדרך.
 *
 * **זו אינה רשימה ריקה, ולכן היא מסוכנת יותר** — מסך מלא בשורות נראה תקין,
 * והשגיאה מתגלה רק כשמישהו מחפש שדה ולא מוצא אותו.
 *
 * ── הכלל שנלמד פעמיים ────────────────────────────────────────────────
 * רישום ישות אינו שלם עד שיש **מסלול קריאה מלא** מן המקור אל `NoteRecord`.
 * שורה ב-`entity_types` נותנת שם. רשומה ב-resolver נותנת מיקום. ממפה הוא
 * מה שנותן **תוכן** — ובלעדיו שתי הראשונות מתארות מחסן שאיש אינו פותח.
 *
 * ⚠️ הממפה גנרי בכוונה — הוא נגזר מ-`EntitySource` ואינו מקודד אף אובייקט
 * של Fireberry. עדשת מראה נוספת לא תדרוש קוד חדש.
 *
 * ⚠️ `fireberry_account` **נשאר** על הממפה הייעודי שלו. הוא נבדק חי, גל
 * אישר שהוא טוען, ואין סיבה להחליף מסלול שעובד (Rule 20 — רדיוס פגיעה).
 *
 * קריאה בלבד. `fb_records` הוא מראה של Fireberry ולא יעד כתיבה.
 */

import type { NoteRecord } from "@/lib/entities/types";
import type { EntitySource } from "@/lib/internal-tools/entitySourceResolver";

type Row = Record<string, unknown>;

const str = (v: unknown): string | undefined =>
  v === null || v === undefined ? undefined : String(v);

export function fireberryRowToNoteRecord(row: Row, source: EntitySource): NoteRecord {
  const idCol = source.idColumn ?? "id";
  const id = str(row[idCol]) ?? "";

  const data = (row[source.metaColumn ?? "data"] ?? {}) as Record<string, unknown>;

  const title = str(source.displayField ? row[source.displayField] : undefined)?.trim();

  // כותרת המשנה עשויה לשבת ב-jsonb ולא בעמודה — לכן מנסים את שניהם.
  const subtitle = source.subtitleField
    ? (str(row[source.subtitleField]) ?? str(data[source.subtitleField]))
    : undefined;

  // ⚠️ כל מפתחות ה-jsonb נכנסים ל-meta, כדי שתצוגה תוכל להציג עמודה בלי
  // שנצטרך להכיר מראש את הסכימה של אובייקט Fireberry כלשהו.
  const meta: Record<string, unknown> = { ...data };

  // ועל גביהם — הכינויים הקריאים שהוגדרו ב-resolver. אלה מה שתצוגה
  // מחפשת לפי שם (`phone`, `role`, `description`), ולא לפי `pcf…`.
  for (const m of source.fieldMappings ?? []) {
    const value =
      "metaKey" in m && m.metaKey !== undefined
        ? data[m.metaKey]
        : "column" in m && m.column !== undefined
          ? row[m.column]
          : undefined;
    if (value !== undefined && value !== null && value !== "") {
      meta[m.contextKey] = value;
    }
  }

  if (subtitle) meta.subtitle = subtitle;

  const created = str(row.created_on) ?? str(data.createdon) ?? "";
  const edited = str(row.modified_on) ?? str(data.modifiedon) ?? created;

  return {
    id,
    title: title || "(ללא שם)",
    // אין גוף TipTap לשורת מראה.
    content: null,
    record_type: source.slug,
    entity_type: source.slug,
    meta,
    status: str(meta.status) ?? "",
    source: "fireberry-mirror",
    // הסינון של שורות מחוקות נעשה בשאילתה כשהעמודה קיימת, לא כאן.
    is_deleted: false,
    created_by: null,
    created_at: created,
    last_edited_at: edited,
  };
}
